import { useShadeContrast } from "@/lib/hooks/use-shade-contrast";
import { cn } from "@lib/utils";
import { Check } from "lucide-react";
import { type CSSProperties } from "react";

type OutOfStockIconProps = React.SVGProps<SVGSVGElement> & {
  strokeColor: string;
};

function OutOfStockIcon({ strokeColor, ...props }: OutOfStockIconProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...props}
      className={cn("w-full h-full z-10", props.className)}
    >
      <path
        d="M2 22 L22 2"
        stroke={strokeColor}
        strokeWidth="1"
        strokeLinecap="round"
      />
    </svg>
  );
}

type SwatchProps = {
  color: string | null;
  outOfStock: boolean;
  name: string;
  shape: "square" | "round";
  selected: boolean;
  container: HTMLElement | null;
} & React.HTMLAttributes<HTMLElement>;

export function Swatch({
  color,
  className,
  outOfStock,
  name,
  shape,
  selected,
  container,
  ...rest
}: SwatchProps) {
  const { iconColor, needsBorder } = useShadeContrast(color, container);

  const defaultClass =
    "text-current grid place-items-center relative after:absolute after:-inset-1 after:border after:border-transparent [&:not([aria-selected='true'])]:hover:ring-1 hover:ring-offset-1 swatch transition-all duration-200 ease-in-out before:absolute before:scale-[90%] before:-inset-0.5 before:border-[--border-color] before:border before:opacity-0 group-aria-checked:before:scale-100 before:transition-transform group-aria-checked:before:opacity-100";

  return (
    <span
      className={cn(
        defaultClass,
        needsBorder && "border border-neutral-500",
        shape === "round" && "rounded-full before:rounded-full",
        className,
      )}
      style={
        { backgroundColor: color, "--border-color": color } as CSSProperties
      }
      {...rest}
    >
      <span className="sr-only">{name}</span>
      {selected && !outOfStock && (
        <Check
          width="24"
          height="24"
          stroke={iconColor === "white" ? "white" : "black"}
          className="w-6 h-6 z-10 animate-checkmark-pop"
        />
      )}

      {outOfStock && (
        <OutOfStockIcon
          className={`${shape === "round" ? "rounded-full" : ""}`}
          strokeColor={iconColor}
        />
      )}
    </span>
  );
}
